import { handleActions } from 'redux-actions';
import {
  pathLoading,
  pathLoaded,
  setFetchPathAfterLogin,
  selectedOptionsChanged,
  additionalDiscussionChanged,
  requestingTimeSlots,
  timeSlotsReceived,
  clearTimeSlots,
  storingReservation,
  reservationStored,
  resetBooking,
  rootLoading,
  rootLoaded,
  setSelectedPath,
  setFetchTimesPermissions,
  setLoginFirst,
  setLoginFirstShowLoginForm,
  setPathNamespace,
  setPathReservationByCodeEnabled,
  setUtilizeEmployerInformation,
  setSelectionHandler,
  setSelectedPathType,
  setRequireSingleEmployer,
  selectManagementOption,
  selectManagementOptionWithSinglePath,
  unselectManagementOptionWithSinglePath,
  setLegalEntity,
  setPathReservationReasonCode,
  openPromptEmployerSelection,
  closePromptEmployerSelection,
  saveAppStateAndSelectionHandler,
  loadStateAndSelectionHandler,
  setSelectedTimeslot,
  unsetSelectedTimeslot
} from '../actions/booking';
import { clearSessionSuccess } from '../actions/session';
import bookingTypes from '../enums/pathTypes';

const initialState = {
  selectedOptionIds: [],
  rootLoading: false,
  rootLoaded: false,
  pathLoading: false,
  pathLoaded: false,
  fetchPathAfterLogin: false,
  pathToLoadAfterLogin: {},
  path: -1,
  managementSelected: false,
  additionalDiscussionChecked: false,
  requestingTimeSlots: false,
  timeSlotsRequested: false,
  timeSlots: {},
  storingReservation: false,
  reservation: {},
  reservationDone: false,
  roots: [],
  canAnonymousFetchTimes: false,
  loginFirst: true,
  loginFirstShowLoginForm: true,
  pathNamespace: '',
  pathReservationByCodeEnabled: false,
  uniquePath: false,
  utilizeEmployerInformation: false,
  selectedPathType: '',
  requireSingleEmployer: false,
  pathBasedBooking: true,
  legalEntity: '',
  shouldSelectEmployer: false,
  savedState: false,
  restoredState: false,
  selectedStoredTimeSlot: {}
};

const bookingReducer = handleActions({
  [rootLoading]: state => ({
    ...state,
    rootLoaded: false,
    rootLoading: true
  }),
  [rootLoaded]: (state, action) => ({
    ...state,
    rootLoaded: true,
    rootLoading: false,
    roots: action.payload,
    uniquePath: action.payload.length === 1
  }),
  [pathLoading]: state => ({
    ...state,
    pathLoaded: false,
    pathLoading: true
  }),
  [setSelectionHandler]: (state, action) => ({
    ...state,
    selectionHandler: action.payload
  }),
  [setPathReservationReasonCode]: (state, action) => ({
    ...state,
    reservationReasonCode: action.payload
  }),
  [setLegalEntity]: (state, action) => ({
    ...state,
    legalEntity: action.payload
  }),
  [pathLoaded]: (state, action) => ({
    ...state,
    pathLoaded: true,
    pathLoading: false,
    selectedOptionIds: []
  }),
  [setFetchPathAfterLogin]: (state, action) => ({
    ...state,
    fetchPathAfterLogin: true,
    pathToLoadAfterLogin: action.payload
  }),
  [setSelectedPath]: (state, action) => ({
    ...state,
    path: action.payload,
    managementSelected: false
  }),
  [selectedOptionsChanged]: (state, action) => ({
    ...state,
    selectedOptionIds: action.payload,
    timeSlotsRequested: false,
    timeSlots: {}
  }),
  [additionalDiscussionChanged]: (state, action) => ({
    ...state,
    additionalDiscussionChecked: action.payload,
    timeSlotsRequested: false,
    timeSlots: initialState.timeSlots
  }),
  [requestingTimeSlots]: state => ({
    ...state,
    requestingTimeSlots: true,
    timeSlotsRequested: true,
    timeSlots: null
  }),
  [timeSlotsReceived]: (state, action) => {
    const timeSlots = action.payload;
    return {
      ...state,
      requestingTimeSlots: false,
      timeSlots
    };
  },
  [clearTimeSlots]: state => ({
    ...state,
    requestingTimeSlots: false,
    timeSlotsRequested: false,
    timeSlots: initialState.timeSlots
  }),
  [storingReservation]: state => ({
    ...state,
    storingReservation: true
  }),
  [reservationStored]: (state, action) => ({
    ...state,
    reservation: action.payload,
    reservationDone: true,
    storingReservation: false
  }),
  [clearSessionSuccess]: state => ({
    ...initialState,
    rootLoaded: true,
    roots: state.roots,
    uniquePath: state.uniquePath,
    pathLoaded: state.uniquePath && !state.fetchPathAfterLogin ? state.pathLoaded : initialState.pathLoaded,
    pathLoading: state.uniquePath ? state.pathLoading : initialState.pathLoading,
    path: state.uniquePath ? state.path : initialState.path,
    selectionHandler: state.selectionHandler,
    managementSelected: false
  }),
  [resetBooking]: state => ({
    ...initialState,
    rootLoaded: true,
    roots: state.roots,
    uniquePath: state.uniquePath,
    pathLoaded: state.uniquePath && !state.fetchPathAfterLogin ? state.pathLoaded : initialState.pathLoaded,
    pathLoading: state.uniquePath ? state.pathLoading : initialState.pathLoading,
    path: state.uniquePath ? state.path : initialState.path,
    selectionHandler: state.selectionHandler,
    managementSelected: false
  }),
  [setFetchTimesPermissions]: (state, action) => ({
    ...state,
    canAnonymousFetchTimes: action.payload
  }),
  [setLoginFirst]: (state, action) => ({
    ...state,
    loginFirst: action.payload
  }),
  [setLoginFirstShowLoginForm]: (state, action) => ({
    ...state,
    loginFirstShowLoginForm: action.payload
  }),
  [setPathNamespace]: (state, action) => ({
    ...state,
    pathNamespace: action.payload
  }),
  [setPathReservationByCodeEnabled]: (state, action) => ({
    ...state,
    pathReservationByCodeEnabled: action.payload
  }),
  [setUtilizeEmployerInformation]: (state, action) => ({
    ...state,
    utilizeEmployerInformation: action.payload
  }),
  [setSelectedPathType]: (state, action) => ({
    ...state,
    selectedPathType: action.payload,
    pathBasedBooking: action.payload === bookingTypes.OCCUPATIONAL || action.payload === bookingTypes.SELF_PAYING
  }),
  [setRequireSingleEmployer]: (state, action) => ({
    ...state,
    requireSingleEmployer: action.payload
  }),
  [selectManagementOption]: state => ({
    ...state,
    path: -1,
    pathLoaded: false,
    managementSelected: true
  }),
  [selectManagementOptionWithSinglePath]: state => ({
    ...state,
    managementSelected: true,
    requestingTimeSlots: false,
    timeSlotsRequested: false,
    timeSlots: initialState.timeSlots
  }),
  [unselectManagementOptionWithSinglePath]: state => ({
    ...state,
    managementSelected: false
  }),
  [openPromptEmployerSelection]: state => ({
    ...state,
    shouldSelectEmployer: true
  }),
  [closePromptEmployerSelection]: state => ({
    ...state,
    shouldSelectEmployer: false
  }),
  [saveAppStateAndSelectionHandler]: state => ({
    ...state,
    savedState: true
  }),
  [loadStateAndSelectionHandler]: state => ({
    ...state,
    savedState: false,
    restoredState: true
  }),
  [setSelectedTimeslot]: (state, action) => ({
    ...state,
    selectedStoredTimeSlot: action.payload
  }),
  [unsetSelectedTimeslot]: state => ({
    ...state,
    selectedStoredTimeSlot: {}
  })
}, initialState);

export default bookingReducer;



// WEBPACK FOOTER //
// ./src/reducers/booking.js